import React, {
  useCallback, useEffect, useState,
} from 'react';
import {
  ArrowDownCircle, Gift, History, Loader2, RefreshCw,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AccessBlockedNotice from '../../components/AccessBlockedNotice';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import client from '../../lib/api';
import ProgressFill from '../../components/ProgressFill';
import StatusBadge from '../../components/StatusBadge';
import RejectReasonNote from '../../components/RejectReasonNote';
import { accountHook } from '../../lib/domHooks';
import PaymentMethodLogo from '../../components/PaymentMethodLogo';
import {
  availableCategories, categoryOf, groupByCategory, sortByCategory,
} from '../../lib/paymentCategories';
import {
  SHOWCASE_UNAVAILABLE_TITLE, isShowcaseMethod, showcaseHasCustomMessage,
  showcaseMessage,
} from '../../lib/showcaseMethod';

const ALL_TAB = { id: 'all', name: 'Tümü' };

const depositStatusMap = {
  onaylandi: { label: 'Onaylandı', color: '#22c55e' },
  beklemede: { label: 'Beklemede', color: '#f59e0b' },
  reddedildi: { label: 'Reddedildi', color: '#e11d48' },
};

const Deposit = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(
    searchParams.get('view') === 'history' ? 'history' : 'new',
  );
  const [tab, setTab] = useState('all');
  const [sel, setSel] = useState(null);
  // Hazir tutar kullanma: kullanici bankadan gonderecegi gercek tutari
  // yazmadan bildirim acamasin. Varsayilan 1.000 TL yanlis taleplere yol
  // aciyordu.
  const [amount, setAmount] = useState('');
  // Göstermelik yöntemin uyarısı pencerede kalıcı olarak durur: toast birkaç
  // saniyede kaybolur ve oyuncu düğmeye tekrar basıp "çalışmıyor" diye
  // bildirirdi.
  const [showcaseNotice, setShowcaseNotice] = useState(false);
  const [methods, setMethods] = useState([]);
  const [depositHistory, setDepositHistory] = useState([]);
  const [activeBonuses, setActiveBonuses] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const { toast } = useToast();

  const loadDeposits = useCallback(async (showLoading = true) => {
    if (showLoading) setHistoryLoading(true);
    try {
      // Süzgeç sunucudadır: casino turları da aynı tabloda olduğu için son
      // 100 satırı çekip istemcide ayıklamak, aktif oyuncuda listeyi boş
      // bırakıyordu.
      const { data } = await client.get('/finance/transactions', {
        params: { limit: 100, type: 'deposit' },
      });
      setDepositHistory(data.items || []);
    } catch (error) {
      if (showLoading) {
        toast({
          title: 'Yatırımlar alınamadı',
          description: error.response?.data?.detail || 'İşlem geçmişi yüklenemedi.',
        });
      }
    } finally {
      if (showLoading) setHistoryLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    client.get('/payments/methods', {
      params: { direction: 'deposit' },
      headers: { 'Cache-Control': 'no-cache' },
    })
      .then(({ data }) => {
        setMethods((data.items || []).map((method) => ({
          ...method,
          duration: 'Yönetici onayı',
          min: `${Number(method.min_amount).toLocaleString('tr-TR')} ₺`,
          max: `${Number(method.max_amount).toLocaleString('tr-TR')} ₺`,
        })));
      })
      .catch(() => setMethods([]));
  }, []);

  useEffect(() => {
    loadDeposits();
  }, [loadDeposits]);

  useEffect(() => {
    client.get('/bonuses/claims/me')
      .then(({ data }) => {
        setActiveBonuses((data.items || []).filter((claimItem) => (
          claimItem.status === 'active'
          && Number(claimItem.wager_required || 0)
            > Number(claimItem.wager_completed || 0)
        )));
      })
      .catch(() => setActiveBonuses([]));
  }, []);

  useEffect(() => {
    if (view !== 'history') return undefined;
    const timer = setInterval(() => loadDeposits(false), 15000);
    return () => clearInterval(timer);
  }, [loadDeposits, view]);

  const changeView = (nextView) => {
    setView(nextView);
    if (nextView === 'history') {
      setSearchParams({ view: 'history' }, { replace: true });
      loadDeposits();
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const continueToPayment = () => {
    if (!sel) return;
    const numericAmount = Number(amount);
    const minimum = Number(sel.min_amount);
    const maximum = Number(sel.max_amount);
    if (
      !Number.isFinite(numericAmount)
      || numericAmount < minimum
      || numericAmount > maximum
    ) {
      toast({
        title: 'Geçersiz tutar',
        description: `Yatırım tutarı ${minimum.toLocaleString('tr-TR')} ₺ ile ${maximum.toLocaleString('tr-TR')} ₺ arasında olmalıdır.`,
      });
      return;
    }
    // Bazı sağlayıcılar (UltraPay) kuruşlu tutar kabul etmez; bayrağı uç
    // bildirir. Kontrol sunucuda da vardır ama oyuncunun ödeme ekranına kadar
    // gelip 400 yemesi kötü bir deneyimdir.
    if (sel.integer_amount_only && !Number.isInteger(numericAmount)) {
      toast({
        title: 'Tutar tam sayı olmalıdır',
        description: `${sel.name} ile kuruşlu tutar gönderilemez.`,
      });
      return;
    }
    // Göstermelik firmanın arkasında hesap yoktur; ödeme sayfasına geçmek
    // oyuncuya boş bir hesap kartı gösterirdi. Uyarı tutar doğrulandıktan
    // sonra çıkar: oyuncu önce girdiği tutarın kabul edildiğini görür.
    if (isShowcaseMethod(sel)) {
      setShowcaseNotice(true);
      toast({
        title: SHOWCASE_UNAVAILABLE_TITLE,
        description: showcaseMessage(sel),
      });
      return;
    }
    const params = new URLSearchParams({
      method: sel.id,
      amount: String(numericAmount),
    });
    navigate(`/hesap/para-yatir/odeme?${params.toString()}`);
  };

  const openPaymentMethod = (method) => {
    setAmount('');
    setShowcaseNotice(false);
    setSel(method);
  };

  const selectedAmount = Number(amount);
  const canContinue = Boolean(sel)
    && amount !== ''
    && Number.isFinite(selectedAmount)
    && selectedAmount >= Number(sel?.min_amount)
    && selectedAmount <= Number(sel?.max_amount);

  // Sekmeler yalnızca gerçekten yöntemi olan bölümler için çizilir: boş bir
  // "Kredi Kartı" sekmesi oyuncuya kapalı değil bozuk görünür.
  const categoryTabs = availableCategories(methods);
  const activeTab = tab !== 'all' && !categoryTabs.some((item) => item.id === tab)
    ? 'all'
    : tab;
  const list = activeTab === 'all'
    ? sortByCategory(methods)
    : methods.filter((method) => categoryOf(method) === activeTab);
  // "Tümü" bölüm başlıklarıyla çizilir; tek bölüm seçiliyken başlık, sekmenin
  // söylediğini tekrar ederdi.
  const groups = activeTab === 'all' ? groupByCategory(list) : null;
  const pendingDepositCount = depositHistory.filter(
    (transaction) => transaction.status === 'beklemede',
  ).length;

  if (user?.deposit_blocked) {
    return (
      <AccessBlockedNotice
        title="Para yatırma işleminiz kısıtlanmıştır"
        description={'Hesabınız yönetici tarafından para yatırma işlemine '
          + 'kapatılmıştır. Bu sayfayı görüntüleyebilirsiniz ancak yeni bir '
          + 'yatırım talebi oluşturamazsınız.'}
      />
    );
  }

  return (
    <div {...accountHook('panel')}>
      <div className="flex items-center gap-3 mb-5" {...accountHook('page-header')}>
        <div className="account-icon-soft w-11 h-11 rounded-xl flex items-center justify-center">
          <ArrowDownCircle size={22} className="text-[var(--pusu-gold)]" />
        </div>
        <div>
          <h1 className="text-2xl font-black gold-text" {...accountHook('title')}>Para Yatır</h1>
          <p className="text-sm text-gray-400" {...accountHook('subtitle')}>
            Yatırım yöntemlerimizle 7/24 hızlı ve güvenli bir şekilde para yatırabilirsiniz.
          </p>
        </div>
      </div>

      {activeBonuses.length > 0 && (
        <div className="pusu-panel mb-5 overflow-hidden" {...accountHook('bonus-claims')}>
          <div className="flex items-center gap-2 border-b border-white/5 bg-[var(--pusu-gold)]/10 px-4 py-3" {...accountHook('header')}>
            <Gift size={17} className="text-[var(--pusu-gold)]" />
            <div>
              <div className="text-sm font-black text-white">Benim Bonusum</div>
              <div className="text-[11px] text-gray-400">Aktif bonus ve kalan çevrim şartınız</div>
            </div>
          </div>
          <div className="grid gap-3 p-4" {...accountHook('list')}>
            {activeBonuses.map((claimItem) => {
              const required = Number(claimItem.wager_required || 0);
              const completed = Number(claimItem.wager_completed || 0);
              const remaining = Math.max(
                0,
                Number(claimItem.wager_remaining ?? required - completed),
              );
              const progress = Math.min(
                100,
                Math.max(
                  0,
                  Number(
                    claimItem.progress_percent
                    ?? (required > 0 ? completed / required * 100 : 100),
                  ),
                ),
              );
              return (
                <div key={claimItem.id} {...accountHook('row')}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-white">{claimItem.bonus_title}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        Bonus: <span className="font-semibold text-green-400">{Number(claimItem.amount || 0).toFixed(2)} ₺</span>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-yellow-500">Kalan Çevrim</div>
                      <div className="text-lg font-black text-yellow-400">{remaining.toFixed(2)} ₺</div>
                    </div>
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/5" {...accountHook('progress')}>
                    <ProgressFill
                      percent={progress}
                      className="h-full rounded-full bg-gradient-to-r from-[#1d9c61] to-[#36d79b]"
                    />
                  </div>
                  <div className="mt-1.5 flex justify-between text-[10px] text-gray-500">
                    <span>{completed.toFixed(2)} ₺ tamamlandı</span>
                    <span>{required.toFixed(2)} ₺ toplam</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="pusu-panel p-2 mb-5 inline-flex flex-wrap gap-2" {...accountHook('tabs')}>
        <button
          type="button"
          {...accountHook('tab')}
          className={`account-tab px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 ${
            view === 'new' ? 'gold-btn' : 'provider-chip'
          }`}
          onClick={() => changeView('new')}
        >
          <ArrowDownCircle size={16} aria-hidden="true" />
          Yeni Yatırım
        </button>
        <button
          type="button"
          {...accountHook('tab')}
          className={`account-tab px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 ${
            view === 'history' ? 'gold-btn' : 'provider-chip'
          }`}
          onClick={() => changeView('history')}
        >
          <History size={16} aria-hidden="true" />
          Yatırımlarım
          {pendingDepositCount > 0 && (
            <span className="min-w-5 h-5 px-1.5 rounded-full bg-amber-400 text-black text-[10px] font-black flex items-center justify-center">
              {pendingDepositCount}
            </span>
          )}
        </button>
      </div>

      {view === 'new' && (
        <>
          <div className="flex gap-2 mb-5 flex-wrap" {...accountHook('filters')}>
            {[ALL_TAB, ...categoryTabs].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                {...accountHook('tab')}
                className={`account-tab px-5 py-2.5 rounded-xl text-sm font-semibold ${
                  activeTab === item.id ? 'gold-btn' : 'provider-chip'
                }`}
              >
                {item.name.toLocaleUpperCase('tr-TR')}
              </button>
            ))}
          </div>

          <div className="pusu-panel overflow-hidden" {...accountHook('methods')}>
            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-xs text-gray-400 uppercase tracking-wider border-b border-white/5">
              <div className="col-span-5">Yöntem</div>
              <div className="col-span-2">Min. Miktar</div>
              <div className="col-span-2">Maks. Miktar</div>
              <div className="col-span-3" />
            </div>
            {(groups || [{ id: activeTab, name: '', methods: list }]).map((group, groupIndex, allGroups) => (
              <div key={group.id}>
                {group.name && (
                  <div className="border-b border-white/5 bg-white/[0.03] px-5 py-2.5 text-[11px] font-black uppercase tracking-wider text-[var(--pusu-gold)]">
                    {group.name}
                  </div>
                )}
                {group.methods.map((method, methodIndex) => (
                  <div
                    key={method.id}
                    data-method-id={method.id}
                    {...accountHook('method')}
                    // Ayraç satır aralarındadır; panelin son satırında
                    // çizilirse kartın alt kenarıyla ikinci bir çizgi olur.
                    className={`grid grid-cols-12 gap-3 items-center px-5 py-4${
                      groupIndex === allGroups.length - 1
                      && methodIndex === group.methods.length - 1
                        ? ''
                        : ' border-b border-white/5'
                    }`}
                  >
                    <div className="col-span-12 md:col-span-5 flex items-center gap-3">
                      <PaymentMethodLogo method={method} size={46} />
                      <div>
                        <div className="text-xs text-gray-500 uppercase">Yöntem:</div>
                        <div className="font-semibold text-sm">{method.name}</div>
                      </div>
                    </div>
                    {/* Başlık satırı yalnızca masaüstünde var; mobilde sütunlar
                        etiketsiz kalıp hangi sayının ne olduğu anlaşılmıyordu. */}
                    <div className="col-span-6 md:col-span-2">
                      <div className="text-[10px] text-gray-500 uppercase md:hidden">Min.</div>
                      <div className="font-semibold text-sm text-white">{method.min}</div>
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <div className="text-[10px] text-gray-500 uppercase md:hidden">Maks.</div>
                      <div className="font-semibold text-sm text-white">{method.max}</div>
                    </div>
                    <div className="col-span-12 md:col-span-3 flex md:justify-end">
                      <button
                        type="button"
                        onClick={() => openPaymentMethod(method)}
                        className="gold-btn px-4 py-2 rounded-lg text-xs w-full md:w-auto"
                      >
                        YATIRIM YAP
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            {list.length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-gray-400" {...accountHook('empty')}>
                Aktif yatırım yöntemi bulunmuyor. Yönetici IBAN veya kripto cüzdanı
                eklediğinde burada görünecektir.
              </div>
            )}
          </div>
        </>
      )}

      {view === 'history' && (
        <div {...accountHook('list')}>
          <div className="flex items-center gap-3 mb-4" {...accountHook('header')}>
            <div>
              <h2 className="text-lg font-bold" {...accountHook('title')}>Yatırımlarım</h2>
              <p className="text-xs text-gray-400">
                Yatırım taleplerinizin güncel durumunu buradan takip edebilirsiniz.
              </p>
            </div>
            <button
              type="button"
              className="provider-chip ml-auto flex items-center gap-2"
              onClick={() => loadDeposits()}
              disabled={historyLoading}
            >
              <RefreshCw
                size={14}
                className={historyLoading ? 'animate-spin' : ''}
                aria-hidden="true"
              />
              Yenile
            </button>
          </div>

          {historyLoading ? (
            <div className="pusu-panel py-12 text-center text-sm text-gray-400" {...accountHook('loading')}>
              <Loader2 size={18} className="inline animate-spin mr-2" />
              Yatırımlar yükleniyor...
            </div>
          ) : depositHistory.length === 0 ? (
            <div className="pusu-panel py-12 px-5 text-center" {...accountHook('empty')}>
              <History size={28} className="mx-auto mb-3 text-gray-500" />
              <div className="font-semibold">Henüz yatırım talebiniz bulunmuyor</div>
              <p className="text-xs text-gray-400 mt-1">
                Yeni Yatırım sekmesinden ilk talebinizi oluşturabilirsiniz.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {depositHistory.map((transaction) => {
                const status = ['expired', 'failed'].includes(transaction.provider_status)
                  ? { label: 'İptal Edildi', color: '#64748b' }
                  : (depositStatusMap[transaction.status] || depositStatusMap.beklemede);
                const method = methods.find(
                  (item) => item.id === transaction.payment_method_id,
                ) || { name: transaction.method };
                return (
                  <div key={transaction.id} className="pusu-panel p-4 sm:p-5" {...accountHook('row')}>
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex items-center gap-3 min-w-0 lg:w-[28%]">
                        <PaymentMethodLogo method={method} size={44} />
                        <div className="min-w-0">
                          <div className="font-semibold truncate">
                            {transaction.method || method.name || 'Yatırım'}
                          </div>
                          <div className="text-[11px] text-gray-500 truncate">
                            İşlem no: {transaction.id}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 flex-1">
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase">Tutar</div>
                          <div className="font-bold text-emerald-400">
                            {Number(transaction.amount).toLocaleString('tr-TR')} ₺
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase">Tarih</div>
                          <div className="text-xs text-gray-300">
                            {new Date(transaction.created_at).toLocaleString('tr-TR')}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase">Durum</div>
                          <StatusBadge
                            color={status.color}
                            status={transaction.status}
                            className="inline-block mt-0.5 px-2 py-1 rounded text-[10px] font-bold"
                          >
                            {status.label}
                          </StatusBadge>
                        </div>
                      </div>
                    </div>
                    <RejectReasonNote transaction={transaction} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === 'new' && sel && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          {...accountHook('modal')}
          onClick={() => { setShowcaseNotice(false); setSel(null); }}
        >
          <div className="account-backdrop absolute inset-0" />
          <div
            className="relative pusu-panel w-full max-w-md p-6"
            {...accountHook('panel')}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <PaymentMethodLogo method={sel} size={48} />
              <div>
                <div className="font-bold">{sel.name}</div>
                <div className="text-xs text-gray-400">
                  {sel.duration} • {sel.min} - {sel.max}
                </div>
              </div>
            </div>
            <label className="text-xs text-gray-400 mb-1.5 block">
              Yatırım Tutarı (₺)
            </label>
            <input
              type="number"
              value={amount}
              min={sel.min_amount}
              max={sel.max_amount}
              inputMode="decimal"
              placeholder="Örn. 500"
              autoFocus
              onChange={(event) => {
                setShowcaseNotice(false);
                setAmount(event.target.value);
              }}
              className="pusu-input mb-3"
              {...accountHook('amount')}
            />
            <p className="mb-3 text-xs text-amber-300">
              Bankadan göndereceğiniz gerçek tutarı aynen girin.
            </p>
            <div className="flex flex-wrap gap-2 mb-4" {...accountHook('quick-amounts')}>
              {[500, 1000, 2500, 5000, 10000]
                .filter((value) => (
                  value >= Number(sel.min_amount) && value <= Number(sel.max_amount)
                ))
                .map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAmount(value)}
                    className="provider-chip"
                  >
                    {value.toLocaleString('tr-TR')} ₺
                  </button>
                ))}
            </div>
            {showcaseNotice ? (
              <div
                role="alert"
                className="account-warning mb-4 p-3 text-xs"
                {...accountHook('notice')}
              >
                {/* Yönetici kendi metnini yazdıysa sabit başlık çizilmez:
                    "Uygun hesap bulunamadı" başlığı onun duyurusuyla
                    çelişebilir. */}
                {!showcaseHasCustomMessage(sel) && (
                  <div className="font-bold mb-1">
                    {SHOWCASE_UNAVAILABLE_TITLE}
                  </div>
                )}
                {showcaseMessage(sel)}
              </div>
            ) : (
              <div className="pusu-card p-3 mb-4 text-xs text-gray-300" {...accountHook('notice')}>
                Ödeme bilgileri bir sonraki sayfada gösterilecektir.
              </div>
            )}
            <div className="flex gap-3" {...accountHook('actions')}>
              <button
                type="button"
                onClick={() => { setShowcaseNotice(false); setSel(null); }}
                className="gold-outline-btn flex-1 py-3 rounded-xl"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={continueToPayment}
                {...accountHook('submit')}
                disabled={!canContinue}
                className="gold-btn flex-1 py-3 rounded-xl disabled:cursor-not-allowed disabled:opacity-40"
              >
                Ödeme Sayfasına Geç
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deposit;
